import type { UIMessage, UIMessageChunk } from 'ai';

import {
  COMMENDA_CHAT_PROFILE_KEY,
  FRESH_TURN_MS,
  LIVE_REPLY_MS,
  MAX_POLLS,
  SERVER_RECOVERED_PROFILE_KEYS,
  SETTLED_MESSAGE_STATUSES,
} from './constants';
import type {
  AgentConversationDataPart,
  AgentConversationEnvelope,
  AgentConversationEnvelopeMessage,
  AgentConversationMessagePart,
  AgentReplyBaseline,
  AgentTurnContent,
} from './types';

export class QueuedConversationTransportHelper {
  static newestAssistantMessage(
    envelope: AgentConversationEnvelope,
    excluded: ReadonlySet<string> = new Set()
  ): AgentConversationEnvelopeMessage | null {
    for (let index = envelope.messages.length - 1; index >= 0; index -= 1) {
      const message = envelope.messages[index];
      if (
        message &&
        message.role === 'ASSISTANT' &&
        !excluded.has(message.messageId)
      )
        return message;
    }

    return null;
  }

  /** Judged on the reply itself: the envelope's state is the latest run's, which until the reply's own
   * run is linked is still the previous turn's. */
  static isReplySettled(reply: AgentConversationEnvelopeMessage): boolean {
    if (reply.liveState === 'RUNNING') return false;
    // Right after a card answer the gate still reads pending until the worker resumes; only the
    // row's own status says the run is parked again.
    if (reply.liveState === 'AWAITING_HUMAN')
      return reply.status === 'AWAITING_CONFIRMATION';
    if (reply.liveState === 'NO_RUN')
      return SETTLED_MESSAGE_STATUSES.has(reply.status);

    return true;
  }

  static hasServerRunRecovery(envelope: AgentConversationEnvelope): boolean {
    const { profile } = envelope.conversation;

    return profile !== null && SERVER_RECOVERED_PROFILE_KEYS.has(profile);
  }

  /** How many reads a poll makes before giving up; unbounded where the server settles every run. */
  static pollBudget(envelope: AgentConversationEnvelope): number {
    return QueuedConversationTransportHelper.hasServerRunRecovery(envelope)
      ? Number.POSITIVE_INFINITY
      : MAX_POLLS;
  }

  /** Null when nothing is in flight. A reply still open is picked back up; a turn whose reply row the
   * queue has not written yet is waited on. */
  static resumeBaseline(
    envelope: AgentConversationEnvelope,
    now: number
  ): AgentReplyBaseline | null {
    const priorMessageIds = new Set(
      envelope.messages.map((message) => message.messageId)
    );
    const ageOf = (message: AgentConversationEnvelopeMessage): number =>
      now - Date.parse(message.createdAt);
    const isFresh = (message: AgentConversationEnvelopeMessage): boolean =>
      ageOf(message) < FRESH_TURN_MS;
    const hasServerRunRecovery =
      QueuedConversationTransportHelper.hasServerRunRecovery(envelope);
    // A gate's reply is as old as the question, and an answer resumes it however late it comes.
    const isAbandoned = (reply: AgentConversationEnvelopeMessage): boolean => {
      if (hasServerRunRecovery) return false;

      return reply.liveState === 'NO_RUN'
        ? !isFresh(reply)
        : reply.liveState === 'RUNNING' && ageOf(reply) >= LIVE_REPLY_MS;
    };

    const reply =
      QueuedConversationTransportHelper.newestAssistantMessage(envelope);
    if (
      reply &&
      !QueuedConversationTransportHelper.isReplySettled(reply) &&
      !isAbandoned(reply)
    ) {
      priorMessageIds.delete(reply.messageId);
      return { priorMessageIds, drawn: reply };
    }

    const newest = envelope.messages[envelope.messages.length - 1];
    if (newest?.role === 'USER' && isFresh(newest))
      return { priorMessageIds, drawn: null };

    return null;
  }

  /** Only chat can rerun a stored turn, and only the turn the server holds: a Retry after a send that
   * never landed has to be sent as the new turn it still is. */
  static canRegenerate(
    envelope: AgentConversationEnvelope,
    lastUserText: string
  ): boolean {
    if (envelope.conversation.profile !== COMMENDA_CHAT_PROFILE_KEY)
      return false;

    const storedUser = [...envelope.messages]
      .reverse()
      .find((message) => message.role === 'USER');

    return (
      storedUser !== undefined &&
      storedUser.content.trim() === lastUserText.trim()
    );
  }

  static getTurnContent(message: UIMessage | undefined): AgentTurnContent {
    const parts = message?.parts ?? [];

    return {
      text: parts
        .map((part) => (part.type === 'text' ? part.text : ''))
        .join(''),
      attachments: parts.flatMap((part) =>
        part.type === 'file'
          ? [
              {
                url: part.url,
                fileName: part.filename ?? 'file',
                mimeType: part.mediaType,
              },
            ]
          : []
      ),
    };
  }

  static toolCallIdOf(part: AgentConversationMessagePart): string | null {
    const candidate = part as { toolCallId?: unknown };

    return typeof candidate.toolCallId === 'string'
      ? candidate.toolCallId
      : null;
  }

  static isSettledToolPart(part: AgentConversationMessagePart): boolean {
    return (
      QueuedConversationTransportHelper.toolCallIdOf(part) !== null &&
      (part as { state?: string }).state !== 'input-available'
    );
  }

  static isDataPart(
    part: AgentConversationMessagePart
  ): part is AgentConversationMessagePart & AgentConversationDataPart {
    return part.type.startsWith('data-');
  }

  /** The output chunk for a tool part past its input, or null for a part that is not a tool call or
   * still waits on its input. */
  static toolOutputChunk(
    part: AgentConversationMessagePart
  ): UIMessageChunk | null {
    const toolCallId = QueuedConversationTransportHelper.toolCallIdOf(part);
    const { state } = part as { state?: string };
    if (toolCallId === null || state === 'input-available') return null;

    return state === 'output-error'
      ? {
          type: 'tool-output-error',
          toolCallId,
          errorText: (part as { errorText?: string }).errorText ?? '',
          dynamic: true,
        }
      : {
          type: 'tool-output-available',
          toolCallId,
          output: (part as { output?: unknown }).output,
          dynamic: true,
        };
  }

  /** A file or tool part → the chunks that rebuild it on the client. Returns nothing for a part the
   * chunk union cannot express, which drops that part rather than failing the turn. */
  static partToChunks(part: AgentConversationMessagePart): UIMessageChunk[] {
    if (part.type === 'file')
      return [
        {
          type: 'file',
          url: part.url,
          mediaType: part.mediaType,
          providerMetadata: part.providerMetadata,
        },
      ];
    if (part.type !== 'dynamic-tool') return [];

    const opened: UIMessageChunk = {
      type: 'tool-input-available',
      toolCallId: part.toolCallId,
      toolName: part.toolName,
      input: part.input,
      dynamic: true,
    };
    const output =
      part.state === 'output-error' || part.state === 'output-available'
        ? QueuedConversationTransportHelper.toolOutputChunk(part)
        : null;

    return output ? [opened, output] : [opened];
  }
}
